/**
 * LeftRailPagesTree — Iter 198d.
 *
 * Persistent Squarespace-style left sidebar with:
 *   • Pages list (jump / add / delete / rename)
 *   • Currently-active page's sections (jump to anchor / reorder shortly)
 *
 * Renders inline (not portalled) so it flexes alongside the canvas. Can be
 * collapsed via the caret button.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import {
  FileText, ChevronRight, Plus, Trash2, Home,
  Layers, PanelLeftClose, PanelLeftOpen, Check, X,
} from 'lucide-react';

function summariseBlock(b) {
  if (!b || typeof b !== 'object') return 'Section';
  const c = b.content || {};
  for (const k of ['title', 'heading', 'headline', 'text', 'label', 'name']) {
    const v = c[k];
    if (typeof v === 'string' && v.trim()) return v.trim().slice(0, 42);
  }
  const t = (b.type || 'section').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export default function LeftRailPagesTree({
  pages = [], activePage, blocks = [],
  switchToPage, onNewPage, onDeletePage, onRenamePage,
  selectedBlockId, onSelectBlock,
  collapsed, onToggleCollapse,
}) {
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const sections = useMemo(() => (
    (blocks || []).filter(b => !b.hidden && b.type !== 'header' && b.type !== 'footer')
  ), [blocks]);

  const submitNew = () => {
    const t = (newTitle || '').trim();
    if (!t) { setShowNew(false); return; }
    onNewPage?.(t);
    setNewTitle('');
    setShowNew(false);
    toast.success(`Page “${t}” added`);
  };

  const submitRename = (page) => {
    const t = (renameValue || '').trim();
    if (!t || t === page.title) { setRenamingId(null); return; }
    onRenamePage?.(page, t);
    setRenamingId(null);
    toast.success('Renamed');
  };

  const scrollToBlock = (id) => {
    onSelectBlock?.(id);
    const el = document.getElementById(`preview-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (collapsed) {
    return (
      <div className="w-9 shrink-0 border-r border-white/[0.06] bg-[#0B0812]/95 flex flex-col items-center py-3">
        <button
          onClick={onToggleCollapse}
          className="w-7 h-7 rounded-md text-white/50 hover:text-white hover:bg-white/[0.06] flex items-center justify-center"
          title="Expand pages"
          data-testid="left-rail-toggle"
        >
          <PanelLeftOpen size={14} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="w-64 shrink-0 border-r border-white/[0.06] bg-[#0B0812]/95 flex flex-col overflow-hidden"
      data-testid="left-rail"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.06]">
        <div className="flex items-center gap-1.5 text-white/60 text-[10px] uppercase tracking-[0.14em] font-bold">
          <Layers size={11} />
          Pages
        </div>
        <button
          onClick={onToggleCollapse}
          className="w-6 h-6 rounded-md text-white/40 hover:text-white hover:bg-white/[0.06] flex items-center justify-center"
          title="Collapse sidebar"
          data-testid="left-rail-toggle"
        >
          <PanelLeftClose size={12} />
        </button>
      </div>

      {/* Pages list */}
      <div className="overflow-y-auto flex-1 py-1.5">
        {/* Home */}
        <PageRow
          icon={<Home size={11} />}
          active={!activePage}
          onClick={() => switchToPage?.(null)}
          testId="left-rail-page-home"
          label="Home"
        />

        {(pages || []).map((page) => (
          <div key={page.id} className="relative group/row">
            {renamingId === page.id ? (
              <div className="flex items-center gap-1 px-2 py-1">
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitRename(page);
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  className="flex-1 h-7 px-2 bg-white/5 border border-white/10 rounded-md text-white text-xs focus:outline-none focus:border-violet-400/50"
                  data-testid="left-rail-rename-input"
                />
                <button onClick={() => submitRename(page)} className="text-emerald-300 hover:text-emerald-200 p-1"><Check size={12} /></button>
                <button onClick={() => setRenamingId(null)} className="text-white/40 hover:text-white/70 p-1"><X size={12} /></button>
              </div>
            ) : (
              <PageRow
                icon={<FileText size={11} />}
                active={activePage?.id === page.id}
                onClick={() => switchToPage?.(page)}
                onDoubleClick={() => { setRenamingId(page.id); setRenameValue(page.title); }}
                testId={`left-rail-page-${page.id}`}
                label={page.title}
                rightExtra={
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePage?.(page);
                    }}
                    className="opacity-0 group-hover/row:opacity-100 text-white/30 hover:text-red-400 p-1 transition-opacity"
                    data-testid={`left-rail-delete-${page.id}`}
                    title="Delete page"
                  >
                    <Trash2 size={11} />
                  </button>
                }
              />
            )}
          </div>
        ))}

        {/* Add page */}
        {showNew ? (
          <div className="flex items-center gap-1 px-2 py-1">
            <input
              autoFocus
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="New page name…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitNew();
                if (e.key === 'Escape') { setShowNew(false); setNewTitle(''); }
              }}
              className="flex-1 h-7 px-2 bg-white/5 border border-white/10 rounded-md text-white text-xs placeholder-white/30 focus:outline-none focus:border-violet-400/50"
              data-testid="left-rail-new-input"
            />
            <button onClick={submitNew} className="text-emerald-300 hover:text-emerald-200 p-1"><Check size={12} /></button>
          </div>
        ) : (
          <button
            onClick={() => setShowNew(true)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-white/45 hover:text-white/85 text-xs transition-colors"
            data-testid="left-rail-new-page"
          >
            <Plus size={11} /> New page
          </button>
        )}

        {/* Sections of active page */}
        <div className="mt-4 px-3 pb-1.5 border-t border-white/[0.06] pt-3">
          <div className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-bold mb-1.5 flex items-center gap-1.5">
            <ChevronRight size={10} /> Sections on this page
          </div>
        </div>
        {sections.length === 0 && (
          <div className="px-3 py-2 text-white/30 text-xs italic">No sections yet</div>
        )}
        {sections.map((b, i) => (
          <button
            key={b.id}
            onClick={() => scrollToBlock(b.id)}
            className={`w-full text-left px-3 py-1.5 flex items-center gap-2 transition-colors text-xs ${
              selectedBlockId === b.id
                ? 'bg-[#8B5CF6]/15 text-white border-l-2 border-[#8B5CF6]'
                : 'text-white/60 hover:text-white hover:bg-white/[0.04] border-l-2 border-transparent'
            }`}
            data-testid={`left-rail-section-${b.id}`}
            title={summariseBlock(b)}
          >
            <span className="w-4 text-white/30 text-[9px] tabular-nums text-right">{i + 1}</span>
            <span className="truncate flex-1">{summariseBlock(b)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function PageRow({ icon, active, onClick, onDoubleClick, testId, label, rightExtra }) {
  return (
    <div className="flex items-stretch">
      <button
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        className={`flex-1 flex items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
          active
            ? 'bg-white/[0.06] text-white'
            : 'text-white/70 hover:text-white hover:bg-white/[0.03]'
        }`}
        data-testid={testId}
      >
        <span className={active ? 'text-[#A78BFA]' : 'text-white/40'}>{icon}</span>
        <span className="truncate">{label}</span>
      </button>
      {rightExtra}
    </div>
  );
}
